/*
 * @Author: Claude
 * @Description: 欺诈通知查询页面API接口
 * @Date: 2023-06-20 14:30:00
 * @Business: 提供欺诈通知查询、导出等相关API服务，支持查询欺诈类型、支付方式等相关信息
 * @Interaction: 作为服务层，提供异步方法调用后端接口，由页面组件调用获取数据或执行导出操作
 */
import Http from '@/utils/http/index';

/**
 * @namespace FraudNotificationAPI
 * @description API服务模块，提供所有欺诈通知相关的后端请求方法
 * @description API service module, providing all fraud notification related backend request methods
 */
export default {
  /**
   * @function queryFraudNotificationList
   * @memberof FraudNotificationAPI
   * @async
   * @description 获取欺诈通知列表数据
   * @description Get fraud notification list data
   * @param {Object} params - 请求参数对象 / Request parameter object
   * @param {number} [params.size] - 每页大小 / Page size
   * @param {number} [params.current] - 当前页码 / Current page
   * @param {string} [params.column] - 排序字段 / Sort column
   * @param {string} [params.asc] - 排序方向 / Sort direction
   * @param {string} [params.serviceId] - 通知ID / Notification ID
   * @param {string} [params.oriTxnId] - 源交易流水号 / Transaction ID
   * @param {string} [params.chargebackId] - 拒付流水号 / Chargeback ID
   * @param {string} [params.refundTxnId] - 退款流水号 / Refund ID
   * @param {string} [params.cardBrand] - 支付方式 / Payment Method
   * @param {string} [params.acqFraudType] - 欺诈类型 / Fraud Type
   * @param {string} [params.chargebackStatus] - 源交易拒付状态 / Dispute Presence
   * @param {string} [params.refundStatus] - 源交易退款类型 / Refund Type
   * @param {string} [params.startCreateTime] - 报告接收开始时间 / Start received time
   * @param {string} [params.endCreateTime] - 报告接收结束时间 / End received time
   * @returns {Promise<Object>} 返回欺诈通知列表数据 / Return fraud notification list data
   */
  async queryFraudNotificationList(params) {
    // 调用HTTP客户端发送GET请求，获取欺诈通知列表数据
    // Call HTTP client to send GET request, get fraud notification list data
    return await Http.get('/merchant/fraud/listPage', { params });
  },

/**
 * 欺诈报告下载
 * @description No description available
 * @id get--merchant-fraud-download
 * @param {Object} params - Request parameters
 * @param {number} params.size - Optional - No description
 * @param {number} params.current - Optional - No description
 * @param {string} params.column - Optional - No description
 * @param {string} params.asc - Optional - No description
 * @param {number} params.serviceId - Optional - 报告ID
 * @param {string} params.institution - Optional - 机构
 * @param {string} params.startReportDate - Optional - 起始欺诈发生日期
 * @param {string} params.endReportDate - Optional - 截止欺诈发生日期
 * @param {string} params.acqFraudType - Optional - 映射后欺诈交易类型
 * @param {number} params.merchantNo - Optional - 商户号
 * @param {number} params.oriTxnId - Optional - 源交易流水号
 * @param {string} params.arn - Optional - ARN
 * @param {string} params.bankOrderNo - Optional - 请求流水ID
 * @param {string} params.outRespOrderNo - Optional - 响应流水号
 * @param {string} params.chargebackStatus - Optional - 源交易拒付状态：-1-无意义; 0-无拒付; 1-有拒付
 * @param {string} params.refundStatus - Optional - 源交易退款状态：-1-无意义; 0-未退款; 1-全部退款; 2-部分退款
 * @param {string} params.mailSendStatus - Optional - 邮件发送状态：0-未发送; 1-已发送
 * @param {string} params.startCreateTime - Optional - 起始创建时间
 * @param {string} params.endCreateTime - Optional - 截止创建时间
 * @param {string} params.cardBrand - Optional - 支付方式
 * @param {string} params.isMerchantReq - Optional - 请求是否来源于商户端
 * @returns {Promise<{
    respCode: string; // No description
    respMsg: string; // No description
    data: {
    }; // No description
  }>} Response object
 */
  async exportFraudNotification(params) {
    // 调用HTTP客户端发送GET请求，获取导出文件数据
    // Call HTTP client to send GET request, get export file data
    return await Http.get('/merchant/fraud/download', {
      params,
      responseType: 'blob', // 设置响应类型为blob，用于文件下载 / Set response type to blob for file download
    });
  },

  /**
   * @function getFraudTypesByCardBrands
   * @memberof FraudNotificationAPI
   * @async
   * @description 根据支付方式获取欺诈类型选项
   * @description Get fraud type options based on payment methods
   * @param {Array} cardBrands - 支付方式代码数组 / Array of payment method codes
   * @returns {Promise<Object>} 返回欺诈类型选项数据 / Return fraud type option data
   */
  async getFraudTypesByCardBrands(params) {
    // 调用HTTP客户端发送GET请求，获取欺诈类型选项数据 / Call HTTP client to send GET request, get fraud type option data
    return await Http.get('/dict/listByParent', { params });
  },
};
